import { useClickAway, useDebounce } from 'ahooks';
import { cx } from '@flowus/common/cx';
import { useEffect, useRef, useState, useMemo } from 'react';
import { TransitionBox } from 'src/common/components/transition-box';
import { Icon } from '@flowus/common/icon-svg/pc-icon';
import { TocItem } from './toc/toc-item';
import type { CSSProperties } from 'react';
import { setAppUiState, useAppUiState } from 'src/services/app/hook';
import { useTocIds } from './toc/use-toc-id';
import { useCurrentSpace } from 'src/hooks/space';
import { useCurrentUser } from 'src/hooks/user';
import { useIsGuest } from 'src/hooks/share/use-permission-utils';
import { Input } from 'src/common/components/input';
import { segmentsToText } from 'src/editor/utils/editor';
import { useObservableStore } from 'src/services/rxjs-redux/hook';
import { Virtuoso } from 'react-virtuoso';

import { ID_PRIVATE_CONTAINER } from 'src/hooks/page/use-dnd/helper';
import { TocGroupContext } from './toc/toc-group-header';
import { TocType } from './toc/types';
import { useOpenModal } from '@flowus/common/next-modal';
import { covertToPrivateListTime } from 'src/common/utils/formatter';

// 定义不同类型的虚拟列表项
interface GroupHeaderItem {
  type: 'header';
  timeGroup: string;
}

interface PageItem {
  type: 'page';
  id: string;
}

// 虚拟列表使用的项目
type VirtualItem = GroupHeaderItem | PageItem;

interface Props {
  style?: CSSProperties;
}
const SORT_TYPE_KEY = 'private-more-list-sort-type';

export const PrivateMoreList = (props: Props) => {
  const { style } = props;
  const ref = useRef(null);
  const show = useAppUiState((state) => state.$showPrivateMore);
  const [mounted, setMounted] = useState(false);
  const [keyword, setKeyword] = useState('');
  const debouncedKeyword = useDebounce(keyword, { wait: 300 });
  const [sortType, setSortType] = useState<'manual' | 'recent'>(() => {
    const sortType = localStorage.getItem(SORT_TYPE_KEY) as 'manual' | 'recent';
    return sortType || 'manual';
  });
  const openModal = useOpenModal();

  const currentSpace = useCurrentSpace();
  const currentUser = useCurrentUser();
  const isGuest = useIsGuest();
  const tocIds = useTocIds(currentSpace.uuid, currentUser.uuid, isGuest);
  const privatePages = tocIds.person || [];

  // 根据搜索关键字过滤页面
  const filteredPages = useObservableStore(
    (state) => {
      let pages = !debouncedKeyword
        ? privatePages
        : privatePages.filter((id) => {
            const block = state.blocks[id];
            if (!block) return false;
            const title = segmentsToText(block.data.segments || []);
            return title.toLowerCase().includes(debouncedKeyword.toLowerCase());
          });

      if (sortType === 'recent') {
        pages = [...pages].sort((a, b) => {
          const blockA = state.blocks[a];
          const blockB = state.blocks[b];
          return (blockB?.updatedAt || 0) - (blockA?.updatedAt || 0);
        });
      }
      return pages;
    },
    [privatePages, debouncedKeyword, sortType]
  );

  // 按时间分组的页面
  const pageGroups = useObservableStore(
    (state) => {
      if (sortType !== 'recent') return null;

      const groups: Record<string, string[]> = {};

      filteredPages.forEach((id) => {
        const block = state.blocks[id];
        if (!block) return;

        const timeDesc = covertToPrivateListTime(block.updatedAt || 0);
        if (!groups[timeDesc]) {
          groups[timeDesc] = [];
        }
        groups[timeDesc]?.push(id);
      });

      return groups;
    },
    [filteredPages, sortType]
  );

  // 时间分组的顺序
  const groupOrder = useMemo(() => {
    if (!pageGroups) return [];

    // 定义时间描述的排序权重
    const timeWeight: Record<string, number> = {
      今天: 0,
      昨天: 1,
      上周: 2,
      更早: 3,
    };

    // 按权重排序
    return Object.keys(pageGroups).sort((a, b) => {
      return (timeWeight[a] ?? 999) - (timeWeight[b] ?? 999);
    });
  }, [pageGroups]);

  // 构建虚拟列表数据
  const virtualItems = useMemo<VirtualItem[]>(() => {
    if (sortType !== 'recent' || !pageGroups) {
      // 手动排序模式，只返回页面项
      return filteredPages.map((id) => ({
        type: 'page',
        id,
      }));
    }

    // 按时间分组模式，将标题和项目创建为扁平数组
    const items: VirtualItem[] = [];

    groupOrder.forEach((timeGroup) => {
      // 先添加组标题
      items.push({
        type: 'header',
        timeGroup,
      });

      // 再添加该组内的所有页面
      const pages = pageGroups[timeGroup];
      if (pages && pages.length > 0) {
        pages.forEach((id) => {
          items.push({
            type: 'page',
            id,
          });
        });
      }
    });

    return items;
  }, [sortType, pageGroups, filteredPages, groupOrder]);

  const handleClose = () => {
    if (!ref.current) return;
    setAppUiState({ $showPrivateMore: false });
  };

  const handleSearchChange = (value: string) => {
    setKeyword(value);
  };

  const handleClearSearch = () => {
    setKeyword('');
  };

  useClickAway(handleClose, ref);

  // 排序方式选项
  const SORT_OPTIONS = [
    { value: 'manual', label: '手动' },
    { value: 'recent', label: '最近编辑' },
  ];

  // 打开排序弹窗
  const openSortModal = (e: React.MouseEvent) => {
    openModal.dropdown({
      popcorn: e.currentTarget as HTMLElement,
      placement: 'bottom',
      content: ({ onCloseModal }) => (
        <div className="next-modal w-[160px] p-1">
          {SORT_OPTIONS.map((option) => (
            <div
              key={option.value}
              className={`rounded text-t2 text-black justify-between w-full flex items-center px-3 h-8 cursor-pointer hover:bg-grey6 ${
                sortType === option.value ? 'text-primary' : ''
              }`}
              onClick={() => {
                localStorage.setItem(SORT_TYPE_KEY, option.value);
                setSortType(option.value as 'manual' | 'recent');
                onCloseModal();
              }}
            >
              {option.label}
              {sortType === option.value && (
                <Icon name="IcCheck02" size="small" className="ml-auto" />
              )}
            </div>
          ))}
        </div>
      ),
    });
  };

  useEffect(() => {
    if (show) {
      setMounted(true);
    }
  }, [show]);

  useEffect(() => {
    // 清空搜索框当面板关闭
    if (!show) {
      setKeyword('');
    }
  }, [show]);

  if (!mounted) return null;

  // 虚拟列表的渲染函数
  const renderVirtualItem = (index: number, item: VirtualItem) => {
    if (item.type === 'header') {
      return <div className="px-4 py-1 text-xs text-grey3 mb-1 mt-4">{item.timeGroup}</div>;
    }
    return <TocItem key={item.id} uuid={item.id} deep={0} showTime={sortType === 'recent'} />;
  };

  return (
    <TocGroupContext.Provider value={TocType.PRIVATE_LIST}>
      <TransitionBox
        domRef={ref}
        className={cx(
          'fixed top-0 left-0 w-80 next-modal max-h-screen h-screen z-[90] animate__animated animate__faster rounded-none',
          show ? 'animate__fadeInLeft' : 'animate__fadeOutLeft'
        )}
        style={style}
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={() => {
          if (!show) {
            setMounted(false);
          }
        }}
      >
        <div className="flex flex-col h-full bg-aside">
          <div className="flex items-center justify-between pl-4 py-2.5 border-b border-grey6">
            <div className="flex items-center justify-between w-full">
              <span className="text-t2-medium">私人</span>

              <div>
                <div
                  className="cursor-pointer p-1 mr-2 hover:animate-hover"
                  onClick={openSortModal}
                >
                  <Icon name="IcSort" size="middle" className="text-grey2" />
                </div>
              </div>
            </div>
          </div>

          <div className="p-2">
            <Input
              autoFocus
              className="h-8"
              value={keyword}
              onChange={handleSearchChange}
              placeholder="搜索页面"
              addonBefore={<Icon size="small" className="ml-1 text-grey4" name="IcSearch" />}
              addonAfter={
                keyword && (
                  <Icon
                    size="small"
                    className="mr-1 text-grey4 cursor-pointer"
                    name="IcUploadCancel"
                    onClick={handleClearSearch}
                  />
                )
              }
            />
          </div>

          <div className="flex-1 ">
            <div className="py-2 h-full">
              {filteredPages.length > 0 ? (
                <Virtuoso
                  id={ID_PRIVATE_CONTAINER}
                  style={{ height: '100%' }}
                  totalCount={virtualItems.length}
                  data={virtualItems}
                  itemContent={renderVirtualItem}
                />
              ) : (
                <div className="flex justify-center items-center py-4 text-grey3">
                  {debouncedKeyword ? '没有找到相关页面' : ''}
                </div>
              )}
            </div>
          </div>
        </div>
      </TransitionBox>
    </TocGroupContext.Provider>
  );
};
